import { Header } from "components/DesignSystem/Header/Header";
import Breadcrumb from "components/DesignSystem/Breadcrumb/Breadcrumb";
import { DashboardLayout } from "components/DashboardLayout";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Loader from "components/DesignSystem/Loader/loader";
import Wind from "components/icons/Wind";
import { AngelSmallRight } from "components/icons/AngelSmallRight";
import { IncorporationFormSource } from "constants/incorporation";
import { useState, ReactNode } from "react";
import { Link } from "react-router-dom";
import { useWindowSize } from "hooks/useWindowSize";
import { useDisableContentTopPadding } from "hooks/useDisableContentTopPadding";
import { IncorporationStageSidebar } from "./IncorporationStageSidebar";
import { IncorporationStageDetail } from "./IncorporationStageDetail";
import { FormTree } from "./useIncorporationFormTree";

const IncorporationNoStageEmptyState = () => (
  <div className="t-flex t-h-full t-flex-col t-items-center t-justify-center t-gap-2 t-text-center">
    <Wind className="t-h-6 t-w-6 t-text-text-30" />
    <span className="t-text-subtitle-sm t-text-text-60">
      Add a stage to start adding sections
    </span>
    <span className="t-text-body-sm t-text-text-30">
      To start adding steps and fields, add a stage to get started
    </span>
  </div>
);

type IncorporationFormBuilderProps = {
  source: IncorporationFormSource;
  contentId: string;
  data: FormTree | undefined;
  isLoading: boolean;
  isSuccess: boolean;
  isEditable: boolean;
  pageTitle: ReactNode;
  breadcrumbName: string;
  breadcrumbLink: string;
  headerRight?: ReactNode;
  footer?: ReactNode;
  isAnswerable?: boolean;
};

export const IncorporationFormBuilder = ({
  source,
  contentId,
  data,
  isLoading,
  isSuccess,
  isEditable,
  pageTitle,
  breadcrumbName,
  breadcrumbLink,
  headerRight,
  footer,
  isAnswerable = true,
}: IncorporationFormBuilderProps) => {
  const [selectedStageId, setSelectedStageId] = useState<string | null>(null);
  const stages = data?.stages || [];
  const activeStageId = selectedStageId ?? stages[0]?.uuid ?? null;
  const activeStage =
    stages.find((stage) => stage.uuid === activeStageId) ?? stages[0];
  const { isMobile } = useWindowSize();
  useDisableContentTopPadding(isMobile);

  let headerLeft = (
    <Breadcrumb.Root>
      <Breadcrumb.Item link={breadcrumbLink} block={false}>
        <span className="t-text-body t-text-neutral-40">{breadcrumbName}</span>
      </Breadcrumb.Item>
      <Breadcrumb.Item block={false}>
        <span className="t-text-body t-font-medium t-text-text-100">
          {pageTitle}
        </span>
      </Breadcrumb.Item>
    </Breadcrumb.Root>
  );

  if (isMobile) {
    headerLeft = (
      <div className="t-flex t-min-w-0 t-flex-col t-gap-0.5">
        <Link
          to={breadcrumbLink}
          className="t-flex t-w-fit t-items-center t-gap-1 t-text-body-sm t-text-neutral-40 t-no-underline hover:t-text-purple"
        >
          {breadcrumbName}
          <AngelSmallRight />
        </Link>
        <span className="t-text-body t-font-medium t-text-text-100">
          {pageTitle}
        </span>
      </div>
    );
  }

  return (
    <DashboardLayout contentProps={{ className: "!t-overflow-hidden" }}>
      <div className="t-flex t-h-full t-min-h-0 t-flex-col">
        <Header v2 title="" left={headerLeft} right={headerRight} />

        <div className="t-flex t-min-h-0 t-flex-1 sm:t-flex-col">
          <Async.Root
            isLoading={isLoading}
            isSuccess={isSuccess}
            isEmpty={false}
            renderOnLoad={
              <div className="t-flex t-h-full t-w-full t-items-center t-justify-center">
                <Loader customType="primary" />
              </div>
            }
          >
            <Async.Success>
              <div className="t-flex t-h-full t-min-h-0 t-w-64 t-shrink-0 t-flex-col t-justify-between sm:t-h-auto sm:t-w-full">
                <div className="t-min-h-0 t-flex-1">
                  <IncorporationStageSidebar
                    stages={stages}
                    selectedStageId={activeStageId}
                    onSelectStage={setSelectedStageId}
                    source={source}
                    parentId={contentId}
                    isEditable={isEditable}
                    isPublished={Boolean(data?.is_published)}
                  />
                </div>
              </div>

              <div className="t-flex t-h-full t-flex-1 t-flex-col sm:t-min-h-0">
                <div className="t-flex t-min-h-0 t-flex-1 t-justify-center t-overflow-y-auto t-pl-9 t-pr-5 t-pb-5 sm:t-px-0">
                  <div className="t-h-full t-w-full t-max-w-[900px]">
                    {activeStage ? (
                      <IncorporationStageDetail
                        key={activeStage.uuid}
                        stage={activeStage}
                        source={source}
                        treeId={contentId}
                        isEditable={isEditable}
                        isAnswerable={isAnswerable}
                        isPublished={Boolean(data?.is_published)}
                        groupId={data?.company_group_id}
                      />
                    ) : (
                      <IncorporationNoStageEmptyState />
                    )}
                  </div>
                </div>

                {footer}
              </div>
            </Async.Success>
          </Async.Root>
        </div>
      </div>
    </DashboardLayout>
  );
};
